import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { Dialog } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { Menu } from 'primeng/menu';
import { CategoriaService } from '@app/shared/components/api/services/categoria.service';
import { Pagination } from '@app/shared/components/api/models/pagination.model';
import { DataVoidComponent } from '@app/shared/components/data-void/data-void.component';
import { ConfirmationService, MessageService, MenuItem } from 'primeng/api';
import { PaginatorControlComponent } from '@app/shared/components/paginator-control/paginator-control.component';

@Component({
  selector: 'app-confiuracoes-categorias',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputText,
    Dialog,
    TableModule,
    TagModule,
    Menu,
    DataVoidComponent,
    PaginatorControlComponent
  ],
  providers: [
    CategoriaService,
  ],
  templateUrl: './confiuracoes-categorias.component.html',
  styleUrls: ['./confiuracoes-categorias.component.scss']
})
export class ConfiuracoesCategoriasComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly categoriaService = inject(CategoriaService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  categoriaForm!: FormGroup;
  pagination = new Pagination<any>();
  loading: boolean = false;
  showCreateDialog: boolean = false;
  IsEditMode: boolean = false;
  openedMenu: Menu | null = null;
  categoriaID: any;

  ngOnInit(): void {
    this.loadCategorias();
    this.initForm();
  }

  private initForm(): void {
    this.categoriaForm = this.fb.group({
      descricao: ['', [Validators.required, Validators.minLength(3)]]
    });
  }

  toggleMenu(event: MouseEvent, menu: Menu): void {
    event.stopPropagation();
    if (this.openedMenu && this.openedMenu !== menu) {
      this.openedMenu.hide();
    }
    this.openedMenu = menu;
    menu.toggle(event);
  }

  getMenuActions(categoria: any): MenuItem[] {
    return [
      {
        label: 'Editar categoria',
        icon: 'pi pi-pencil',
        command: () => this.editCateforia(categoria)
      },
      {
        label: categoria.ativo ? 'Desativar' : 'Ativar',
        icon: categoria.ativo ? 'pi pi-times' : 'pi pi-check',
        command: () => this.toggleStatus(categoria)
      }
    ];
  }

  loadCategorias(): void {
    this.loading = true;
    const params = {
      PageSize: this.pagination.pageSize || 5,
      PageNumber: this.pagination.pageNumber || 1,
      OrderBy: 'id desc'
    };

    this.categoriaService.list(params).subscribe({
      next: (res) => {
        this.pagination = new Pagination<any>().deserialize(res);
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        console.error(err);
        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: err?.error?.message || 'Ocorreu um erro inesperado',
           life: 5000
        });
      }
    });
  }

  toggleDialog(): void {
    this.showCreateDialog = !this.showCreateDialog;
  }

  getSeverity(ativo: boolean): 'success' | 'danger' {
    return ativo ? 'success' : 'danger';
  }

  saveCategory(): void {
    if (this.categoriaForm.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulário inválido',
        detail: 'Preencha corretamente o campo descrição',
         life: 5000
      });
      return;
    }

    this.loading = true;
    const payload = { id: 0, descricao: this.categoriaForm.value.descricao.trim() };

    this.categoriaService.store(payload).subscribe({
      next: () => {
        this.loading = false;

        this.toggleDialog();
        this.categoriaForm.reset();
        this.loadCategorias();

        this.messageService.add({
          severity: 'success',
          summary: 'Categoria criada',
          detail: 'Categoria salva com sucesso!',
           life: 5000
        });
      },
      error: (err) => {
        this.loading = false;
        console.error('Erro ao criar categoria', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro ao criar categoria',
          detail: err?.error?.retorno?.mensagem || 'Tente novamente mais tarde.',
           life: 5000
        });
      }
    });
  }

  editCateforia(categoria: any): void {
    this.IsEditMode = true;
    this.categoriaID = categoria?.id;
    this.categoriaForm.patchValue({
      descricao: categoria?.descricao,
    });
    this.toggleDialog();
  }

  UpdateCategoria(): void {
    if (this.categoriaForm.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulário inválido',
        detail: 'Preencha corretamente o campo descrição',
         life: 5000
      });
      return;
    }

    this.loading = true;
    const payload = { id: this.categoriaID, descricao: this.categoriaForm.value.descricao.trim() };

    this.categoriaService.update(payload).subscribe({
      next: () => {
        this.loading = false;
        this.toggleDialog();
        this.categoriaForm.reset();
        this.loadCategorias();

        this.messageService.add({
          severity: 'success',
          summary: 'Categoria Editada',
          detail: 'Categoria editada com sucesso!',
           life: 5000
        });
      },
      error: (err) => {
        this.loading = false;
        console.error('Erro ao editar categoria', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro ao editar categoria',
          detail: err?.error?.retorno?.mensagem || 'Tente novamente mais tarde.',
           life: 5000
        });
      }
    });
  }

  toggleStatus(categoria: any): void {
    this.confirmationService.confirm({
      message: `Tem certeza que deseja ${categoria.ativo ? 'desativar' : 'ativar'} esta categoria?`,
      header: 'Confirmação',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: `Sim, ${categoria.ativo ? 'Desativar' : 'Ativar'}`,
      },
      accept: () => {

        this.categoriaService.toggleStatus(categoria?.id).subscribe({
          next: (res) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: `Categoria ${categoria.ativo ? 'desativada' : 'ativada'} com sucesso!`,
              life: 5000,
            });
            this.loadCategorias();
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Erro',
              detail: `Ocorreu um erro ao ${categoria.ativo ? 'desativar' : 'ativar'} a categoria`,
              life: 5000,
            });
            console.error(err);
          }
        });
      },
      reject: () => {
      },
    });
  }

  onPageChange(event: any): void {
    this.pagination.pageNumber = event.page + 1;
    this.pagination.pageSize = event.rows;
    this.loadCategorias();
  }

  isInvalid(field: string): boolean {
    const control = this.categoriaForm.get(field);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
}
